import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, startTransition, Dispatch, SetStateAction, ReactNode } from "react";
import {
    DataGrid as FluentDataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Menu, MenuItem, MenuList, MenuPopover,
    Skeleton, SkeletonItem,
    Spinner, Tooltip,
    createTableColumn,
    Text as FluentText,
    Button,
    tokens,
} from "@fluentui/react-components";
import type {
    TableColumnDefinition,
    TableColumnSizingOptions,
} from "@fluentui/react-components";
import { Icon, IconButton } from "@fluentui/react";
import { compareSortKeys, isEmptySortKey, DataGridSortKey } from "./DataGridSortUtils";
import { useDataGridGroups } from "./useDataGridGroups";

// -------------------------------------------------------------------
// Public interfaces
// -------------------------------------------------------------------

const headerCellStyle: React.CSSProperties = {
    alignItems: "center",
    display: "flex",
    fontWeight: 600,
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    width: "100%",
};
const headerContentStyle: React.CSSProperties = {
    alignItems: "center",
    display: "flex",
    gap: 4,
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    width: "100%",
};
const headerTextStyle: React.CSSProperties = {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};
const headerTooltipIconStyle: React.CSSProperties = {
    cursor: "help",
    display: "inline-flex",
    flexShrink: 0,
};
const defaultColumnWidth = 100;
const defaultColumnPadding = 16;
const selectionCellWidth = 44;
const contextMenuColumnKey = "__contextMenu";
const contextMenuColumnWidth = 25;
const contextMenuColumnTotalWidth = contextMenuColumnWidth + defaultColumnPadding;
const stableResizableColumnsOptions = { autoFitColumns: false };

export type SelectionMode = "none" | "single" | "multiselect";

export interface DataGridV9Column<TItem> {
    key: string;
    name: string;
    /** Custom cell renderer. */
    onRender?: (item: TItem) => React.ReactNode;
    /** Sort key extractor. */
    getSortKey?: (item: TItem) => DataGridSortKey;
    /** Disables sorting for this column. */
    notSortable?: boolean;
    /** Custom comparison function (bypasses getSortKey). */
    customCompare?: (a: TItem, b: TItem) => number;
    /** Tooltip shown next to the header. */
    tooltip?: string;
    /** Minimum column width in px. */
    minWidth?: number;
    /** Maximum column width in px. */
    maxWidth?: number;
    /** Default / ideal column width in px. */
    defaultWidth?: number;
    /** Field name for auto-sorting when getSortKey is not provided. */
    fieldName?: string;
    /** Whether this column should allow text wrapping instead of ellipsis truncation. */
    isMultiline?: boolean;
}

export interface SortState {
    columnKey: string;
    isSortedDescending: boolean;
}

export interface ContextMenuItem {
    key: string;
    text: string;
    iconName?: string;
    onClick?: (item: any) => void;
}

export interface DataGridV9Props<TItem> {
    columns: DataGridV9Column<TItem>[];
    items: TItem[];

    // ---- Loading ----
    /** Whether the grid is loading. */
    isLoading?: boolean;

    // ---- Sorting ----
    /** Initial sort state. */
    initialSort?: SortState;
    /** External sort state callback (overrides built-in sorter). */
    setSortState?: (value: SortState) => void;

    // ---- Selection ----
    /** Selection mode: "none", "single", or "multiselect". Defaults to "none". */
    selectionMode?: SelectionMode;
    /** Callback that receives selected items. */
    setSelectedItems?: Dispatch<SetStateAction<TItem[]>>;
    /** Default selection keys to apply once on mount. */
    defaultSelectedKeys?: string[];
    /** Prevents clicks on input elements (checkboxes, buttons) from changing row selection. */
    disableAutoSelectOnInputElements?: boolean;

    // ---- Layout ----
    /** Unique key extractor per row. */
    getRowId?: (item: TItem) => string;
    /** Additional style on the root element. */
    style?: React.CSSProperties;
    /** Controls header visibility (defaults to true). */
    isHeaderVisible?: boolean;
    /** Use compact row height. */
    compact?: boolean;

    // ---- Grouping ----
    /** Gets the group display name for an item. */
    getItemGroupName?: (item: TItem) => string;
    /** Group display names in desired sort order. */
    groupSortOrder?: string[];
    /** Whether to show groups with no items. */
    showEmptyGroups?: boolean;
    /** Return true if a group should start collapsed. */
    isGroupCollapsed?: (group: string) => boolean;

    // ---- Infinite scroll ----
    /** Whether there are more items to load. */
    hasMoreItems?: boolean;
    /** Function to call to load more items. */
    loadMore?: () => void;
    /** Whether to load all items. */
    loadAll?: boolean;

    // ---- Context menu ----
    /** Context menu items for right-click. */
    contextMenuItems?: ContextMenuItem[];
    /** Disables context menu entirely. */
    noContextMenu?: boolean;
    /** Hides the context menu shortcut button on rows. */
    noContextMenuShortcut?: boolean;

    // ---- UI chrome ----
    /** Item to pin as the first row regardless of sort. */
    stickyItem?: TItem;
    /** Message or content to show when the grid has no rows. */
    noRowsMessage?: string | React.ReactNode;
    /** Hides the summary text. */
    noSummary?: boolean;
    /** Hides the "x items selected" text in summary. */
    hideSummarySelectionText?: boolean;
    /** Provides custom summary text. */
    getSummary?: (isSorting: boolean) => string;
    /** Callback for when a row has been invoked (double-click). */
    onItemInvoked?: (item: TItem) => void;

    // -------------------------------------------------------------------
    // NOT SUPPORTED in V9 — these props are intentionally omitted.
    // Consumers needing these should use DataGridV8 or migrate to v9 native.
    //
    // - selectionZoneProps: v9 has no SelectionZone concept
    // - canSelectItem / itemShouldAppearDimmed: no per-row selection gating
    // - renderDetailsHeader / renderDetailsFooter / onRenderRow: different render architecture
    // - noScrollablePane / noVerticalFill / scrollablePaneStyles: v9 uses CSS, no ScrollablePane
    // - layoutMode: v9 uses CSS flex natively
    // - groupProps: v9 uses custom group rendering
    // - checkboxVisibility: v9 manages this via selectionMode
    // -------------------------------------------------------------------
}

// -------------------------------------------------------------------
// Column cache — ensures columns created from the same input array
// always return the SAME TableColumnDefinition[] reference.
// This is critical for v9's useTableColumnSizing_unstable to work.
// -------------------------------------------------------------------
// Column cache key includes context menu config to avoid stale columns
// when menu props change while columns array identity stays the same.
interface ColumnCacheEntry<TItem> {
    columns: TableColumnDefinition<TItem>[];
    noContextMenu: boolean;
    noContextMenuShortcut: boolean;
    hasContextMenuItems: boolean;
}
const columnCache = new WeakMap<DataGridV9Column<any>[], ColumnCacheEntry<any>>();

interface ColumnSizingSpec {
    key: string;
    minWidth: number;
    baseWidth: number;
    maxWidth?: number;
    canGrow: boolean;
}

function getColumnSizingSpecs<TItem>(
    columns: DataGridV9Column<TItem>[],
    includeContextMenuColumn: boolean,
): ColumnSizingSpec[] {
    const specs = columns.map(col => {
        const minWidth = Math.max(col.minWidth ?? defaultColumnWidth, 1);
        const maxWidth = col.maxWidth !== undefined ? Math.max(col.maxWidth, minWidth) : undefined;
        const configuredWidth = col.defaultWidth ?? maxWidth ?? minWidth;
        const baseWidth = Math.max(maxWidth !== undefined ? Math.min(configuredWidth, maxWidth) : configuredWidth, minWidth);

        return {
            key: col.key,
            minWidth,
            baseWidth,
            maxWidth,
            canGrow: maxWidth === undefined,
        };
    });

    if (includeContextMenuColumn) {
        specs.push({
            key: contextMenuColumnKey,
            minWidth: contextMenuColumnTotalWidth,
            baseWidth: contextMenuColumnTotalWidth,
            maxWidth: contextMenuColumnTotalWidth,
            canGrow: false,
        });
    }

    return specs;
}

function distributeExtraWidth(specs: ColumnSizingSpec[], widths: number[], extraWidth: number) {
    const growableIndexes = specs
        .map((spec, index) => ({ spec, index }))
        .filter(({ spec }) => spec.canGrow)
        .map(({ index }) => index);

    if (!growableIndexes.length) {
        return;
    }

    const totalWeight = growableIndexes.reduce((sum, index) => sum + widths[index], 0) || growableIndexes.length;
    let remainingWidth = extraWidth;
    growableIndexes.forEach((index, growableIndex) => {
        const addedWidth = growableIndex === growableIndexes.length - 1
            ? remainingWidth
            : Math.floor(extraWidth * (widths[index] / totalWeight));
        widths[index] += addedWidth;
        remainingWidth -= addedWidth;
    });
}

function shrinkToFit(specs: ColumnSizingSpec[], widths: number[], overflowWidth: number) {
    let remainingOverflow = overflowWidth;
    for (let index = widths.length - 1; index >= 0 && remainingOverflow > 0; index--) {
        const availableShrink = widths[index] - specs[index].minWidth;
        if (availableShrink <= 0) {
            continue;
        }

        const shrinkBy = Math.min(availableShrink, remainingOverflow);
        widths[index] -= shrinkBy;
        remainingOverflow -= shrinkBy;
    }
}

/**
 * Build stable sizing options from the caller's min/default/max intent.
 * Width is distributed once before the grid paints; Fluent's internal auto-fit is disabled.
 */
function buildSizingOptions<TItem>(
    columns: DataGridV9Column<TItem>[],
    containerWidth: number | undefined,
    selectionMode: SelectionMode,
    includeContextMenuColumn: boolean,
): TableColumnSizingOptions {
    const opts: TableColumnSizingOptions = {};
    const specs = getColumnSizingSpecs(columns, includeContextMenuColumn);
    const widths = specs.map(spec => spec.baseWidth);

    if (containerWidth && containerWidth > 0 && specs.length) {
        const reservedWidth = (selectionMode === "none" ? 0 : selectionCellWidth) + specs.length * defaultColumnPadding;
        const availableColumnWidth = Math.max(containerWidth - reservedWidth, 0);
        const totalWidth = widths.reduce((sum, width) => sum + width, 0);

        if (availableColumnWidth > totalWidth) {
            distributeExtraWidth(specs, widths, availableColumnWidth - totalWidth);
        } else if (availableColumnWidth < totalWidth) {
            shrinkToFit(specs, widths, totalWidth - availableColumnWidth);
        }
    }

    for (const [index, spec] of specs.entries()) {
        const width = Math.max(Math.round(widths[index]), spec.minWidth);
        opts[spec.key] = {
            minWidth: spec.minWidth,
            defaultWidth: width,
            idealWidth: width,
            padding: defaultColumnPadding,
        };
    }

    return opts;
}

function getOrBuildV9Columns<TItem>(
    columns: DataGridV9Column<TItem>[],
    noContextMenu: boolean,
    noContextMenuShortcut: boolean,
    contextMenuItems: ContextMenuItem[] | undefined,
    onContextMenuShortcutClick: (item: TItem) => (e: any) => void,
    sortDirectionRef: { current: "ascending" | "descending" }
): TableColumnDefinition<TItem>[] {
    const cached = columnCache.get(columns);
    const hasItems = !!contextMenuItems?.length;
    if (cached && cached.noContextMenu === noContextMenu && cached.noContextMenuShortcut === noContextMenuShortcut && cached.hasContextMenuItems === hasItems) {
        return cached.columns;
    }
    const result = buildV9Columns(columns, noContextMenu, noContextMenuShortcut, contextMenuItems, onContextMenuShortcutClick, sortDirectionRef);
    columnCache.set(columns, { columns: result, noContextMenu, noContextMenuShortcut, hasContextMenuItems: hasItems });
    return result;
}

// -------------------------------------------------------------------
// Column builder (outside component for stable reference)
// -------------------------------------------------------------------

function buildV9Columns<TItem>(
    columns: DataGridV9Column<TItem>[],
    noContextMenu: boolean,
    noContextMenuShortcut: boolean,
    contextMenuItems: ContextMenuItem[] | undefined,
    onContextMenuShortcutClick: (item: TItem) => (e: any) => void,
    sortDirectionRef: { current: "ascending" | "descending" }
): TableColumnDefinition<TItem>[] {
    const cols = columns.map((col) => {
        let compareFunc: ((a: TItem, b: TItem) => number) | undefined;
        if (!col.notSortable) {
            if (col.getSortKey) {
                compareFunc = (a, b) => {
                    const ka = col.getSortKey!(a), kb = col.getSortKey!(b);
                    const emptyA = isEmptySortKey(ka), emptyB = isEmptySortKey(kb);
                    if (emptyA && emptyB) return 0;
                    // v9 multiplies compare result by -1 for descending.
                    // To keep empties at the bottom regardless, we counteract the flip.
                    const emptyLast = sortDirectionRef.current === "descending" ? -1 : 1;
                    if (emptyA) return emptyLast;
                    if (emptyB) return -emptyLast;
                    return compareSortKeys(ka, kb);
                };
            } else if (col.customCompare) {
                compareFunc = (a, b) => col.customCompare!(a, b);
            } else if (col.fieldName) {
                compareFunc = (a, b) => {
                    const ka = (a as any)[col.fieldName!], kb = (b as any)[col.fieldName!];
                    const emptyA = isEmptySortKey(ka), emptyB = isEmptySortKey(kb);
                    if (emptyA && emptyB) return 0;
                    const emptyLast = sortDirectionRef.current === "descending" ? -1 : 1;
                    if (emptyA) return emptyLast;
                    if (emptyB) return -emptyLast;
                    return compareSortKeys(ka, kb);
                };
            } else if (col.onRender) {
                compareFunc = (a, b) => {
                    const va = col.onRender!(a);
                    const vb = col.onRender!(b);
                    const sa = typeof va === 'string' ? va : String(va ?? '');
                    const sb = typeof vb === 'string' ? vb : String(vb ?? '');
                    return sa.localeCompare(sb);
                };
            }
        }
        return createTableColumn<TItem>({
            columnId: col.key,
            compare: compareFunc,
            renderHeaderCell: () => <HeaderContent name={col.name} tooltip={col.tooltip} />,
            renderCell: (item) => col.onRender ? col.onRender(item) : null,
        });
    });

    if (!noContextMenu && !noContextMenuShortcut && contextMenuItems?.length) {
        cols.push(createTableColumn<TItem>({
            columnId: contextMenuColumnKey,
            renderHeaderCell: () => "",
            renderCell: (item) => (
                <IconButton
                    iconProps={{ iconName: "More" }}
                    onClick={onContextMenuShortcutClick(item)}
                    styles={{ root: { height: 18, width: 25 } }}
                />
            ),
        }));
    }

    return cols;
}

function HeaderContent({ name, tooltip }: { name: string; tooltip?: string }) {
    return (
        <span style={headerContentStyle}>
            <span style={headerTextStyle} title={name}>{name}</span>
            {tooltip && (
                <Tooltip content={tooltip} relationship="description">
                    <span style={headerTooltipIconStyle} onClick={ev => ev.stopPropagation()}>
                        <Icon iconName="Info" styles={{ root: { fontSize: 12 } }} />
                    </span>
                </Tooltip>
            )}
        </span>
    );
}

// -------------------------------------------------------------------
// Component implementation
// -------------------------------------------------------------------

function DataGridV9Impl<TItem>(props: DataGridV9Props<TItem>) {
    const {
        columns, items, isLoading: isLoadingProp, initialSort,
        setSortState: setSortStateExternal,
        selectionMode: selectionModeProp, setSelectedItems, defaultSelectedKeys,
        disableAutoSelectOnInputElements = false,
        getRowId, style,
        isHeaderVisible = true, compact = false,
        getItemGroupName, groupSortOrder, showEmptyGroups, isGroupCollapsed,
        hasMoreItems, loadMore, loadAll: alwaysLoadAll,
        contextMenuItems, noContextMenu = true, noContextMenuShortcut = true,
        stickyItem, noRowsMessage, noSummary = true, hideSummarySelectionText, getSummary,
        onItemInvoked,
    } = props;

    const isLoading = isLoadingProp ?? false;
    const selectionMode = selectionModeProp ?? (setSelectedItems ? "multiselect" : "none");

    // ---- Auto-detect row ID from common properties if getRowId not provided ----
    const effectiveGetRowId = useMemo(() => {
        if (getRowId) return getRowId;
        // Auto-detect: try 'key', 'id' properties for stable identity
        return (item: TItem) => {
            const a = item as any;
            if (a.key !== undefined) return String(a.key);
            if (a.id !== undefined) return String(a.id);
            return undefined as any; // fall back to v9's internal index-based key
        };
    }, [getRowId]);

    // ---- Stable refs for callbacks that shouldn't trigger re-renders ----
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const getRowIdRef = useRef(effectiveGetRowId);
    getRowIdRef.current = effectiveGetRowId;
    const setSelectedItemsRef = useRef(setSelectedItems);
    setSelectedItemsRef.current = setSelectedItems;
    const noSummaryRef = useRef(noSummary);
    noSummaryRef.current = noSummary;

    // ---- Sort state ----
    const [desiredSortState, setDesiredSortState] = useState<SortState | undefined>(initialSort);
    const sortState = !setSortStateExternal && hasMoreItems ? undefined : desiredSortState;
    // Track sort direction for nulls-last logic in compare functions.
    // v9 multiplies compare results by -1 for descending, so we counteract it.
    const sortDirectionRef = useRef<"ascending" | "descending">(
        initialSort?.isSortedDescending ? "descending" : "ascending"
    );

    const onSortChange = useCallback((_e: any, nextState: any) => {
        sortDirectionRef.current = nextState.sortDirection;
        setDesiredSortState({
            columnKey: nextState.sortColumn,
            isSortedDescending: nextState.sortDirection === "descending",
        });
    }, []);

    useEffect(() => {
        if (setSortStateExternal && desiredSortState) {
            setSortStateExternal(desiredSortState);
        }
    }, [setSortStateExternal, desiredSortState]);

    // ---- Grouping (declared early — needed by selection logic) ----
    const isGrouped = !!getItemGroupName;

    // ---- Selection ----
    // Uncontrolled selection — v9 manages checkbox visuals internally (instant response).
    // We only listen for changes to notify the parent via startTransition.
    const selectedCountRef = useRef(defaultSelectedKeys?.length ?? 0);
    const [selectedCountForSummary, setSelectedCountForSummary] = useState(defaultSelectedKeys?.length ?? 0);
    const defaultSelectedItems = useMemo(
        () => defaultSelectedKeys ? new Set(defaultSelectedKeys) : undefined,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const onSelectionChange = useCallback((_e: any, data: any) => {
        const newKeys = data.selectedItems as Set<any>;
        selectedCountRef.current = newKeys.size;
        // Deferred: parent notification + summary (low-priority)
        startTransition(() => {
            if (setSelectedItemsRef.current) {
                const currentItems = itemsRef.current;
                const getId = getRowIdRef.current;
                const selected = currentItems.filter((_item, index) => {
                    const key = getId ? getId(_item) : String(index);
                    return newKeys.has(key);
                });
                setSelectedItemsRef.current(selected);
            }
            if (!noSummaryRef.current) {
                setSelectedCountForSummary(newKeys.size);
            }
        });
    }, []); // stable — reads current values from refs

    // ---- Sort items ----
    // v9 handles sorting internally via column compare functions
    // (its column sizing depends on internal sort state management)
    const sortedItems = items;

    // ---- Sticky item ----
    const processedItems = useMemo(() => {
        return stickyItem ? [stickyItem, ...sortedItems] : sortedItems;
    }, [sortedItems, stickyItem]);

    // ---- Group collapse toggle ----
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
        if (!isGroupCollapsed || !groupSortOrder) return new Set();
        return new Set(groupSortOrder.filter(g => isGroupCollapsed(g)));
    });

    const toggleGroupCollapse = useCallback((groupName: string) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupName)) {
                next.delete(groupName);
            } else {
                next.add(groupName);
            }
            return next;
        });
    }, []);

    const { groupedRows: effectiveGroupedRows } = useDataGridGroups(processedItems, {
        getGroupName: getItemGroupName || (() => ""),
        groupOrder: groupSortOrder,
        showEmpty: showEmptyGroups,
        // Don't filter collapsed items — DataGridBody needs all items for selection tracking.
        // Collapse is handled visually via display:none in the render function.
    });

    // ---- Grouped items for DataGridBody ----
    // Build maps: itemKey → groupName for first item (header injection),
    // and itemKey → groupName for all items (collapse check).
    const { groupedBodyItems, groupHeaderMap, groupCounts, itemGroupMap } = useMemo(() => {
        if (!isGrouped) return { groupedBodyItems: processedItems, groupHeaderMap: new Map<string, string>(), groupCounts: new Map<string, number>(), itemGroupMap: new Map<string, string>() };
        const bodyItems: TItem[] = [];
        const headerMap = new Map<string, string>();
        const allItemGroups = new Map<string, string>();
        const counts = new Map<string, number>();
        let lastGroup: string | undefined;
        for (const row of effectiveGroupedRows) {
            if (row.type === "header") {
                counts.set(row.groupName, row.count);
                continue;
            }
            const item = row.item;
            const group = getItemGroupName!(item);
            const key = effectiveGetRowId ? effectiveGetRowId(item) : String(bodyItems.length);
            allItemGroups.set(key, group);
            if (group !== lastGroup) {
                headerMap.set(key, group);
                lastGroup = group;
            }
            bodyItems.push(item);
        }
        return { groupedBodyItems: bodyItems, groupHeaderMap: headerMap, groupCounts: counts, itemGroupMap: allItemGroups };
    }, [effectiveGroupedRows, isGrouped, effectiveGetRowId, getItemGroupName, processedItems]);

    // ---- Context menu shortcut click handler ----
    const [contextMenuTarget, setContextMenuTarget] = useState<{ item: TItem; x: number; y: number } | undefined>();

    const onContextMenuShortcutClick = useCallback(
        (item: TItem) => (e: any) => {
            e.stopPropagation();
            const rect = (e.target as HTMLElement).getBoundingClientRect();
            setContextMenuTarget({ item, x: rect.left, y: rect.bottom });
        },
        []
    );

    // ---- Build v9 column definitions ----
    // Use module-level cache to ensure stable column identity across renders.
    // v9's useTableColumnSizing_unstable requires columns to maintain referential identity.
    const hasContextMenuShortcutColumn = !noContextMenu && !noContextMenuShortcut && !!contextMenuItems?.length;
    const v9Columns = getOrBuildV9Columns(columns, noContextMenu, noContextMenuShortcut, contextMenuItems, onContextMenuShortcutClick, sortDirectionRef);

    // ---- Container measurement ----
    // Keep the measured element in state because the loading skeleton and full grid wrappers
    // can be different DOM nodes as the component transitions from loading to loaded.
    const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
    const setContainerRef = useCallback((node: HTMLDivElement | null) => {
        setContainerElement(node);
    }, []);
    const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
    const updateContainerWidth = useCallback((width: number | undefined) => {
        if (!width || width <= 0) return;
        setContainerWidth((prev) => {
            if (prev === undefined) return width;
            // Update if width changed by more than 50px (flex layout redistribution)
            if (Math.abs(prev - width) > 50) return width;
            return prev;
        });
    }, []);

    useLayoutEffect(() => {
        if (!containerElement) return;

        const readWidth = () => containerElement.getBoundingClientRect().width || containerElement.offsetWidth;
        updateContainerWidth(readWidth());

        let measureFrame: number | undefined;
        let stabilizeFrame: number | undefined;
        const scheduleStabilizedMeasure = () => {
            if (measureFrame !== undefined || stabilizeFrame !== undefined) return;
            measureFrame = requestAnimationFrame(() => {
                measureFrame = undefined;
                const firstWidth = readWidth();
                if (firstWidth <= 0) return;
                stabilizeFrame = requestAnimationFrame(() => {
                    stabilizeFrame = undefined;
                    const secondWidth = readWidth();
                    updateContainerWidth(secondWidth > 0 ? secondWidth : firstWidth);
                });
            });
        };

        const resizeObserver = new ResizeObserver(scheduleStabilizedMeasure);
        resizeObserver.observe(containerElement);

        let intersectionObserver: IntersectionObserver | undefined;
        if (typeof IntersectionObserver !== "undefined") {
            intersectionObserver = new IntersectionObserver(scheduleStabilizedMeasure);
            intersectionObserver.observe(containerElement);
        }

        return () => {
            resizeObserver.disconnect();
            intersectionObserver?.disconnect();
            if (measureFrame !== undefined) {
                cancelAnimationFrame(measureFrame);
            }
            if (stabilizeFrame !== undefined) {
                cancelAnimationFrame(stabilizeFrame);
            }
        };
    }, [containerElement, updateContainerWidth]);

    // ---- Column sizing ----
    const columnSizingOptions = useMemo<TableColumnSizingOptions>(
        () => buildSizingOptions(columns, containerWidth, selectionMode, hasContextMenuShortcutColumn),
        [columns, containerWidth, selectionMode, hasContextMenuShortcutColumn]
    );

    // ---- Infinite scroll ----
    const [loadAllState, setLoadAllState] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (loadAllState || alwaysLoadAll) {
            if (hasMoreItems && loadMore) {
                loadMore();
            } else {
                setLoadAllState(false);
            }
        }
    }, [loadAllState, hasMoreItems, loadMore, alwaysLoadAll]);

    useEffect(() => {
        if (!hasMoreItems || !loadMore || !sentinelRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !isLoading) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasMoreItems, loadMore, isLoading, containerWidth]);

    // ---- Context menu ----
    const onRowContextMenu = useCallback(
        (item: TItem) => (e: React.MouseEvent) => {
            if (noContextMenu || !contextMenuItems?.length) return;
            e.preventDefault();
            setContextMenuTarget({ item, x: e.clientX, y: e.clientY });
        },
        [noContextMenu, contextMenuItems]
    );

    // ---- Summary ----
    const summary: ReactNode = useMemo(() => {
        if (noSummary) return null;

        if (loadAllState) {
            return (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <Spinner size="tiny" label={`Fetching all items (${processedItems.length} loaded so far)...`} labelPosition="after" />
                    <Button appearance="transparent" onClick={() => setLoadAllState(false)}>Cancel</Button>
                </div>
            );
        }

        let summaryText: string;
        if (isLoading) {
            summaryText = "Loading...";
        } else if (getSummary) {
            summaryText = getSummary(!!sortState);
        } else {
            summaryText = hasMoreItems
                ? `Showing first ${processedItems.length} items${sortState ? " (sorted)" : ""}.`
                : `Showing all ${processedItems.length} items${sortState ? " (sorted)" : ""}.`;
        }

        if (selectedCountForSummary > 1 && !hideSummarySelectionText) {
            summaryText += ` (${selectedCountForSummary} selected)`;
        }

        return summaryText ? <FluentText size={200} style={{ marginTop: 10, display: "block" }}>{summaryText}</FluentText> : null;
    }, [noSummary, loadAllState, isLoading, getSummary, sortState, hasMoreItems, processedItems.length, selectedCountForSummary, hideSummarySelectionText]);

    // ---- Empty state ----
    const isEmpty = !isLoading && !processedItems.length;

    // ---- Prevent auto-select on input elements ----
    // Intercept click on the content wrapper div. When disableAutoSelectOnInputElements is true,
    // clicking on inputs/buttons/checkboxes stops propagation so v9's row selection handler
    // (on DataGridRow) never fires. V9's own selection checkbox is in DataGridSelectionCell
    // (separate from DataGridCell), so it's unaffected.
    const onContentClick = useCallback((e: React.MouseEvent) => {
        if (!disableAutoSelectOnInputElements) return;
        const target = e.target as HTMLElement;
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "BUTTON" || tag === "TEXTAREA" || tag === "SELECT"
            || target.closest("[role='checkbox']") || target.closest("[role='switch']")
            || target.closest(".ms-Checkbox")) {
            e.stopPropagation();
        }
    }, [disableAutoSelectOnInputElements]);

    // ---- Row styles ----
    const rowStyle: React.CSSProperties = {
        alignItems: "center",
    };

    // ---- Cell content styles (per-column: ellipsis or multiline) ----
    const cellContentStyleMap = useMemo(() => {
        const ellipsisStyle: React.CSSProperties = {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            width: "100%",
        };
        const multilineStyle: React.CSSProperties = {
            minWidth: 0,
            width: "100%",
            whiteSpace: "normal",
            wordBreak: "break-word",
        };
        const map = new Map<string, React.CSSProperties>();
        for (const col of columns) {
            map.set(col.key, col.isMultiline ? multilineStyle : ellipsisStyle);
        }
        return map;
    }, [columns]);

    // ---- Shimmer column flex values ----
    const shimmerFlexValues = useMemo(() => {
        return columns.map(col => ({
            key: col.key,
            flex: col.defaultWidth || col.maxWidth || col.minWidth || 100,
        }));
    }, [columns]);

    // ---- Loading (shimmer rows) ----
    if (isLoading && !processedItems.length) {
        const shimmerRows = Array.from({ length: 8 });
        return (
            <div ref={setContainerRef} style={{ minWidth: 0 }}>
                {shimmerRows.map((_, i) => (
                    <Skeleton key={i} style={{ display: "flex", gap: 12, padding: compact ? "4px 12px" : "8px 12px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
                        {shimmerFlexValues.map((col) => (
                            <SkeletonItem key={col.key} style={{ flex: col.flex, height: 16 }} />
                        ))}
                    </Skeleton>
                ))}
            </div>
        );
    }

    // ---- Render group header ----
    const renderGroupHeader = (groupName: string, count: number, isCollapsed: boolean) => (
        <div
            style={{
                padding: "8px 12px",
                fontWeight: 600,
                fontSize: 13,
                backgroundColor: tokens.colorNeutralBackground3,
                borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                gridColumn: `1 / -1`,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
            }}
            onClick={() => toggleGroupCollapse(groupName)}
        >
            <Icon iconName={isCollapsed ? "ChevronRight" : "ChevronDown"} styles={{ root: { fontSize: 12 } }} />
            {groupName} ({count})
        </div>
    );

    const hasMeasuredContainerWidth = containerWidth !== undefined;

    // Keep the measuring wrapper mounted, but avoid painting the full grid until sizing is known.
    return (
        <>
            {summary}
            <div ref={setContainerRef} style={{ minWidth: 0 }}>
                {hasMeasuredContainerWidth ? (
                    <>
                        <FluentDataGrid
                            items={isGrouped ? groupedBodyItems : processedItems}
                            columns={v9Columns}
                            getRowId={effectiveGetRowId}
                            sortable
                            defaultSortState={initialSort ? {
                                sortColumn: initialSort.columnKey,
                                sortDirection: initialSort.isSortedDescending ? "descending" : "ascending",
                            } : undefined}
                            onSortChange={onSortChange}
                            selectionMode={selectionMode === "none" ? undefined : selectionMode}
                            defaultSelectedItems={defaultSelectedItems}
                            onSelectionChange={selectionMode !== "none" ? onSelectionChange : undefined}
                            resizableColumns
                            resizableColumnsOptions={stableResizableColumnsOptions}
                            columnSizingOptions={columnSizingOptions}
                            size={compact ? "small" : "medium"}
                            style={{ width: "100%", minWidth: 0, ...style }}
                        >
                            {isHeaderVisible && (
                                <DataGridHeader>
                                    <DataGridRow>
                                        {({ renderHeaderCell }) => (
                                            <DataGridHeaderCell>
                                                <span style={headerCellStyle}>{renderHeaderCell()}</span>
                                            </DataGridHeaderCell>
                                        )}
                                    </DataGridRow>
                                </DataGridHeader>
                            )}

                            <DataGridBody<TItem>>
                                {({ item, rowId }) => {
                                    const rowKey = String(rowId);
                                    const groupName = isGrouped ? groupHeaderMap.get(rowKey) : undefined;
                                    const itemGroup = isGrouped ? itemGroupMap.get(rowKey) : undefined;
                                    const isCollapsedRow = itemGroup !== undefined && collapsedGroups.has(itemGroup);
                                    return (
                                        <>
                                            {groupName !== undefined && renderGroupHeader(groupName, groupCounts.get(groupName) ?? 0, collapsedGroups.has(groupName))}
                                            {!isCollapsedRow && (
                                                <DataGridRow<TItem>
                                                    key={rowId}
                                                    style={rowStyle}
                                                    onDoubleClick={() => onItemInvoked?.(item)}
                                                    onContextMenu={onRowContextMenu(item)}
                                                >
                                                    {({ renderCell, columnId }) => (
                                                        <DataGridCell>
                                                            <div onClick={onContentClick} style={cellContentStyleMap.get(columnId as string)}>{renderCell(item)}</div>
                                                        </DataGridCell>
                                                    )}
                                                </DataGridRow>
                                            )}
                                        </>
                                    );
                                }}
                            </DataGridBody>
                        </FluentDataGrid>

                        {isEmpty && (
                            <div style={{ padding: "16px 60px" }}>
                                {noRowsMessage || "No items to display."}
                            </div>
                        )}

                        {hasMoreItems && (
                            <>
                                <div ref={sentinelRef} style={{ height: 1 }} />
                                {isLoading && (
                                    <Skeleton style={{ display: "flex", gap: 12, padding: "8px 12px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
                                        {shimmerFlexValues.map((col) => (
                                            <SkeletonItem key={col.key} style={{ flex: col.flex, height: 16 }} />
                                        ))}
                                    </Skeleton>
                                )}
                            </>
                        )}

                        {contextMenuTarget && !noContextMenu && contextMenuItems?.length && (
                            <Menu open onOpenChange={() => setContextMenuTarget(undefined)} positioning={{
                                target: { getBoundingClientRect: () => ({ top: contextMenuTarget.y, left: contextMenuTarget.x, bottom: contextMenuTarget.y, right: contextMenuTarget.x, width: 0, height: 0, x: contextMenuTarget.x, y: contextMenuTarget.y, toJSON: () => ({}) }) } as any,
                            }}>
                                <MenuPopover>
                                    <MenuList>
                                        {contextMenuItems.map(mi => (
                                            <MenuItem
                                                key={mi.key}
                                                icon={mi.iconName ? <Icon iconName={mi.iconName} /> : undefined}
                                                onClick={() => {
                                                    mi.onClick?.(contextMenuTarget.item);
                                                    setContextMenuTarget(undefined);
                                                }}
                                            >
                                                {mi.text}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        )}
                    </>
                ) : null}
            </div>
        </>
    );
}

const typedMemo: <T>(c: T) => T = React.memo;
export const DataGridV9 = typedMemo(DataGridV9Impl);
